/* eslint-disable sonarjs/cognitive-complexity */
import {
  BaSeAutocomplete,
  BaSeButton,
  BaSeGrid,
  BaSeGridItem,
  BaSeHeading6,
  BaSeInput,
  BaSeOptionPicker,
  BaSeParagraph,
  BaSeTag,
  BaSeTheme,
} from '@base/react';
import * as Sebrae from '@sebrae/react-core';
import React from 'react';

import { useCityController } from '../../../../../hooks/controllers/city-controller';
import { useUfController } from '../../../../../hooks/controllers/uf-controller';
import { useMessage } from '../../../../../hooks/messages/useMessage';
import { useManageClassService } from '../../../../../hooks/services/manage-class-service';
import { useRegionalService } from '../../../../../hooks/services/regional-service';
import { Container, ContainerButton, ContentList } from './regional-style';

const cache = Sebrae.StorageUtil.inTemporaryStore();
const cacheKeySebrae = '@empretec:sebrae';

function RegionalForm({ mode = 'create', closeModal, data = {} }) {
  const isEdit = mode === 'edit';

  const { getUfs } = useUfController();
  const { getCitiesByUf, rawCities } = useCityController();
  const {
    saveReginal,
    updateReginal,
    returnValidateRegionalName,
    returnOrganizationalUnit,
    returnProjectByUf,
    returnActionByProject,
  } = useRegionalService();
  const { handleAttention, handleAttentionFast, handleError, handleSuccess } =
    useMessage();
  const { getManagerbYiDuF } = useManageClassService();
  const { hasRole } = Sebrae.useAuth();

  const [listUfs, setListUfs] = React.useState([]);
  const [ufSelected, setUfSelected] = React.useState(
    isEdit && data.uf
      ? [{ id: data.uf.id, label: data.uf.name, sigla: data.uf.initials }]
      : [],
  );
  const [ufDefault, setUfDefault] = React.useState([]);
  const [listCitiesSelected, setListCitiesSelected] = React.useState(
    (data.cities ?? []).map((city) => ({ ...city, label: city.name })),
  );
  const [listManagersSelected, setListManagersSelected] = React.useState(
    (data.responsibleManagers ?? []).map((manager) => ({
      ...manager,
      label: manager.name,
    })),
  );
  const [organizationalUnit, setOrganizationalUnit] = React.useState(
    data.codigoUnidadeOrganizacionalFoco
      ? [{
        codigoUnidadeOrganizacionalFoco: data.codigoUnidadeOrganizacionalFoco,
        id: data.codigoUnidadeOrganizacionalFoco,
        name: data.nameUnidadeOrganizacionalFoco,
        label: data.nameUnidadeOrganizacionalFoco,
      }]
      : [],
  );

  const [regionalName, setRegionalName] = React.useState(data.name ?? '');
  const [handleConfirmSave, setHandleConfirmSave] = React.useState(false);
  const [saveLoading, setSaveLoading] = React.useState(false);
  const [saveMode, setSaveMode] = React.useState('');
  const [nameIsValid, setNameIsValid] = React.useState(false);
  const [projectSelected, setProjectSelected] = React.useState(
    isEdit && data.project
      ? { ...data.project, id: data.project.codeProjectFoco, label: data.project.nameProjectFoco }
      : undefined,
  );
  const [actionSelected, setActionSelected] = React.useState(
    isEdit && data.action
      ? { ...data.action, id: data.action.codeActionFoco, label: data.action.nameActionFoco }
      : undefined,
  );
  const [projectKey, setProjectKey] = React.useState(0);
  const [actionKey, setActionKey] = React.useState(0);


  const isNationalAdmin = React.useMemo(() => {
    return cache.getItem(cacheKeySebrae) === 'NA' || hasRole('ADMINISTRADOR');
  }, [hasRole]);

  const currentUf = React.useMemo(() => {
    return isNationalAdmin ? ufSelected : ufDefault;
  }, [isNationalAdmin, ufSelected, ufDefault]);

  const ufDisabled = React.useMemo(() => {
    return !isNationalAdmin;
  }, [isNationalAdmin]);

  React.useEffect(() => {
    if (isEdit && data.uf) {
      getCitiesByUf({ id: data.uf.id });
    }

    const load = async () => {
      try {
        const response = await getUfs();
        setListUfs(response);

        const cachedSigla = cache.getItem(cacheKeySebrae);
        if (cachedSigla !== 'NA') {
          const ufDef = response.find((res) => res.sigla === cachedSigla);
          if (ufDef) {
            setUfDefault([ufDef]);
            if (!isEdit) {
              getCitiesByUf({ id: ufDef.id });
            }
          }
        }
      } catch {
        setListUfs([]);
      }
    };
    load();
  }, [isEdit, data.uf, getCitiesByUf, getUfs]);

  const handleAutocompleteSearchManager = React.useCallback(
    async (term) => {
      if (term.trim().length < 3) return [];
      try {
        const idUf = currentUf[0]?.id;
        if (!idUf) return [];

        const response = await getManagerbYiDuF({
          pageIndex: 0,
          pageSize: 100,
          nameSearch: term,
          idUf,
        });

        return response.items.map((item) => ({
          ...item,
          id: item.id,
          label: item.name,
        }));
      } catch {
        return [];
      }
    },
    [getManagerbYiDuF, currentUf],
  );

  const handleSelectUf = (values) => {
    if (values.length === 0) return;
    const prevId = currentUf[0]?.id;
    if (values[0].id === prevId) return;
    getCitiesByUf({ id: values[0].id });
    setUfSelected(values);
    setOrganizationalUnit([]);
    setProjectSelected(undefined);
    setActionSelected(undefined);
    setProjectKey((k) => k + 1);
    setActionKey((k) => k + 1);
    setListCitiesSelected([]);
  };

  const handleAutocompleteSearchProject = React.useCallback(
    async (term) => {
      if (term.trim().length < 3) return [];
      try {
        const idUf = currentUf[0]?.id;
        if (!idUf) return [];

        const response = await returnProjectByUf(term, currentUf[0].sigla);

        const responseFormated = response.map((item) => ({
          ...item,
          id: item.codProjeto,
          label: item.nomeProjeto,
        }));

        return responseFormated;
      } catch {
        return [];
      }
    },
    [returnProjectByUf, currentUf],
  );

  const handleAutocompleteSearchUnitiOrganizational = React.useCallback(
    async (term) => {
      try {
        if (!term || term.trim().length < 3) {
          return;
        };
        const sigla = currentUf[0].sigla

        const response = await returnOrganizationalUnit({
          nameSearch: term,
        }, sigla);

        const responseFormated = response.map((item) => ({
          ...item,
          id: item.codigoUnidadeOrganizacionalFoco,
          label: item.name,
        }));

        return responseFormated;
      } catch {
        return []
      } finally {
      }
    },
    [returnOrganizationalUnit, currentUf],
  );

  const handleAutocompleteSearchAction = React.useCallback(
    async (nameSearch) => {
      if (nameSearch.trim().length < 3 || !projectSelected.id) return [];
      try {
        const response = await returnActionByProject(projectSelected.id, nameSearch);
        const responseFormated = response.map((item) => ({
          ...item,
          id: item.id,
          label: item.nomeAcao,
        }));

        return responseFormated;
      } catch {
        return [];
      }
    },
    [returnActionByProject, projectSelected],
  );


  const handleSelectChange = (values) => {
    setListCitiesSelected((prev) => {
      const newItems = values.filter(
        (val) => !prev.some((item) => item.id === val.id),
      );
      return [...prev, ...newItems];
    });
  };

  const handleRemoveTagCity = (id) => {
    setListCitiesSelected((prev) => prev.filter((item) => item.id !== id));
  };

  const handleRemoveTagManager = (id) => {
    setListManagersSelected((prev) => prev.filter((item) => item.id !== id));
  };

  const validateForm = React.useCallback(() => {
    const name = regionalName.trim();
    if (!name || name.length > 100) return false;
    if (!currentUf?.length) return false;
    if (!listCitiesSelected.length) return false;
    if (!projectSelected) return false;
    if (!actionSelected) return false;
    if (!organizationalUnit.length) return false;

    return true;
  }, [currentUf, organizationalUnit, regionalName, projectSelected, actionSelected, listCitiesSelected.length]);

  const validateRegionalName = React.useCallback(async () => {
    try {
      if (!currentUf?.length) return false;

      const isDuplicate = await returnValidateRegionalName(
        currentUf[0].id,
        regionalName,
      );

      if (isDuplicate) {
        setNameIsValid(true);
        handleAttentionFast(
          'Já existe uma regional nessa UF cadastrada com esse nome.',
        );
        return false;
      }

      setNameIsValid(false);
      return true;
    } catch (error) {
      handleError(error.message);
      return false;
    }
  }, [currentUf, returnValidateRegionalName, regionalName, handleAttentionFast, handleError]);

  const handleSave = React.useCallback(
    async (action) => {
      try {
        if (!validateForm()) {
          handleAttention('Campo obrigatório não informado.');
          return;
        }

        if (!isEdit) {
          const isValid = await validateRegionalName();
          if (!isValid) return;
        }

        setHandleConfirmSave(true);
        setSaveMode(action);
      } catch (error) {
        setNameIsValid(false);
        handleError(error.message);
      }
    },
    [isEdit, validateForm, handleAttention, validateRegionalName, handleError],
  );

  const confirmSaveRegional = React.useCallback(async () => {
    try {
      if (!currentUf?.length) return;
      setSaveLoading(true)
      const dto = {
        uf: currentUf[0].id,
        cities: listCitiesSelected.map(({ id }) => id),
        name: regionalName.trim(),
        responsibleManagers: listManagersSelected.map(({ id }) => id),
        codigoUnidadeOrganizacionalFoco: organizationalUnit[0].codigoUnidadeOrganizacionalFoco,
        nameUnidadeOrganizacionalFoco: organizationalUnit[0]?.name,
        codeProjectFoco: projectSelected.id,
        codeActionFoco: actionSelected.id
      };

      if (isEdit) {
        await updateReginal(dto, data.id);
      } else {
        await saveReginal(dto);
      }

      handleSuccess('Operação realizada com sucesso!');
      closeModal(saveMode);
    } catch (error) {
      handleError(error.message);
    } finally {
      setSaveLoading(false)
    }
  }, [
    isEdit,
    currentUf,
    listCitiesSelected,
    regionalName,
    listManagersSelected,
    updateReginal,
    saveReginal,
    data,
    handleSuccess,
    closeModal,
    saveMode,
    handleError,
    projectSelected,
    actionSelected,
    organizationalUnit
  ]);

  const showConfirm = isEdit ? handleConfirmSave : !nameIsValid && handleConfirmSave;

  if (showConfirm) {
    return (
      <>
        <BaSeGrid gap={24}>
          <BaSeGridItem size={12}>
            <BaSeHeading6
              color={BaSeTheme.colors.feedbackInterface.atencao55}
              isBold={true}
            >
              Atenção!
            </BaSeHeading6>
          </BaSeGridItem>

          <BaSeGridItem size={12}>
            <BaSeParagraph color="#636363">
              <strong>
                Tem certeza que deseja {isEdit ? 'editar' : 'criar'} a regional{' '}
                {regionalName.trim()}?
              </strong>
            </BaSeParagraph>
          </BaSeGridItem>

          <BaSeGridItem size={12}>
            <Container>
              <BaSeParagraph color="#636363">UF:</BaSeParagraph>
              <BaSeParagraph color="#8B99A7">
                {currentUf[0]?.label}
              </BaSeParagraph>
            </Container>
          </BaSeGridItem>

          <BaSeGridItem size={12}>
            <BaSeParagraph color="#636363">Cidades:</BaSeParagraph>
            <ContentList>
              <BaSeParagraph color="#8B99A7">
                {listCitiesSelected.map((item) => item.label).join(', ')}.
              </BaSeParagraph>
            </ContentList>
          </BaSeGridItem>

          {listManagersSelected.length > 0 && (
            <BaSeGridItem size={12}>
              <BaSeParagraph color="#636363">
                Gestores vinculados:
              </BaSeParagraph>
              <ContentList>
                <BaSeParagraph color="#8B99A7">
                  {listManagersSelected.map((item) => item.label).join(', ')}
                </BaSeParagraph>
              </ContentList>
            </BaSeGridItem>
          )}

          <BaSeGridItem size={12}>
            <BaSeParagraph color="#636363">Unidade Organizacional (FOCO):</BaSeParagraph>
            <ContentList>
              <BaSeParagraph color="#8B99A7">
                {organizationalUnit[0]?.label ?? '—'}
              </BaSeParagraph>
            </ContentList>
          </BaSeGridItem>

          <BaSeGridItem size={12}>
            <BaSeParagraph color="#636363">Projeto:</BaSeParagraph>

            <ContentList>
              <BaSeParagraph color="#8B99A7">{projectSelected.label ?? ''}</BaSeParagraph>
            </ContentList>
          </BaSeGridItem>

          <BaSeGridItem size={12}>
            <BaSeParagraph color="#636363">Ação:</BaSeParagraph>
            <ContentList>
              <BaSeParagraph color="#8B99A7">{actionSelected.label ?? ''}</BaSeParagraph>
            </ContentList>
          </BaSeGridItem>
        </BaSeGrid>

        <ContainerButton>
          <BaSeButton
            value="Confirmar"
            color={BaSeTheme.colors.defaultColors.black}
            onClick={confirmSaveRegional}
            isLoading={saveLoading}
          />
          <BaSeButton
            isDisabled={saveLoading}
            value="Revisar"
            type="secondary"
            onClick={() => setHandleConfirmSave(false)}
          />
        </ContainerButton>
      </>
    );
  }

  return (
    <>
      <BaSeGrid gap={24}>
        <BaSeGridItem size={12}>
          <BaSeInput
            label="Nome da regional:"
            onChange={setRegionalName}
            value={regionalName}
            maxLength={100}
            overrideColors={{
              border: !isEdit && nameIsValid ? '#ff0000' : '',
            }}
            placeholder="Nome da regional"
          />
        </BaSeGridItem>

        <BaSeGridItem size={12}>
          {ufDisabled ? (
            <BaSeOptionPicker
              maxMenuHeight="100px"
              isDisabled
              label="UF:"
              values={ufDefault}
              key="UF:"
              maxOptionsSelected={1}
              initialSelectedValuesId={ufDefault.map((i) => i.id)}
              emptyValueLabel="UF:"
            />
          ) : (
            <BaSeOptionPicker
              label="UF:"
              maxMenuHeight="100px"
              maxOptionsSelected={1}
              values={listUfs}
              emptyValueLabel="Selecione"
              onChange={handleSelectUf}
              initialSelectedValuesId={ufSelected.map((v) => v.id)}
            />
          )}
        </BaSeGridItem>

        <BaSeGridItem size={12}>
          <BaSeOptionPicker
            label="Cidades:"
            maxMenuHeight="100px"
            maxOptionsSelected={1}
            values={rawCities}
            emptyValueLabel="Selecione uma ou mais cidades"
            searchPlaceholder="Selecione uma ou mais cidades"
            onChange={handleSelectChange}
            initialSelectedValuesId={listCitiesSelected.map((v) => v.id)}
          />
        </BaSeGridItem>

        {listCitiesSelected.length > 0 && (
          <BaSeGridItem size={12}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {listCitiesSelected.map((s) => (
                <BaSeTag
                  key={s.id}
                  color="default"
                  limitedWidth={200}
                  label={s.label}
                  onClose={() => handleRemoveTagCity(s.id)}
                  size="small"
                  themeMode="light"
                  type="primary"
                />
              ))}
            </div>
          </BaSeGridItem>
        )}

        <BaSeGridItem size={12}>
          <BaSeAutocomplete
            inputMode="search"
            label="Gestores vinculados:"
            minLengthToSearch={3}
            mode="list"
            onSearchTerm={handleAutocompleteSearchManager}
            onSelectedItem={(item, clear) => {
              setListManagersSelected((prevManagers) => {
                const existingIds = new Set(prevManagers.map((m) => m.id));
                if (!existingIds.has(item.id)) {
                  return [...prevManagers, item];
                }
                return prevManagers;
              });
              clear('');
            }}
            placeholder="Selecione um ou mais gestores"
            subLabel=""
          />
        </BaSeGridItem>

        {listManagersSelected.length > 0 && (
          <BaSeGridItem size={12}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {listManagersSelected.map((s) => (
                <BaSeTag
                  key={s.id}
                  color="default"
                  limitedWidth={200}
                  label={s.label}
                  onClose={() => handleRemoveTagManager(s.id)}
                  size="small"
                  themeMode="light"
                  type="primary"
                />
              ))}
            </div>
          </BaSeGridItem>
        )}

        {/*   <BaSeGridItem size={12}>
          <BaSeOptionPicker
            label="Unidade Organizacional (FOCO):"
            maxMenuHeight="100px"
            values={listUnitOrg}
            emptyValueLabel="Selecione uma unidade organizacional"
            onChange={handleSelectUnitOrg}
            maxOptionsSelected={1}
            withFilterText
            searchPlaceholder='Buscar Unidade Organizacional'
            isLoading={listUnitOrgLoading}
            loadingText='Carregando Unidades Organizacionais'
            initialSelectedValuesId={unitOrgSelect.map((v) => v.id)}
          />
        </BaSeGridItem> */}

        <BaSeGridItem size={12}>
          <BaSeAutocomplete
            key={projectKey}
            label="Unidade Organizacional (FOCO):"
            inputMode="search"
            minLengthToSearch={3}
            isDisabled={currentUf.length === 0}
            mode="list"
            onSearchTerm={handleAutocompleteSearchUnitiOrganizational}
            onSelectedItem={(item, clear) => {
              setOrganizationalUnit([item]);
              clear('');
            }}
            placeholder="Selecione uma unidade organizacional"
            subLabel=""
          />
        </BaSeGridItem>

        {organizationalUnit.length > 0 && (
          <BaSeGridItem size={12}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {organizationalUnit.map((s) => (
                <BaSeTag
                  key={s.id}
                  color="default"
                  limitedWidth={300}
                  label={s.label}
                  onClose={() => setOrganizationalUnit([])}
                  size="small"
                  themeMode="light"
                  type="primary"
                />
              ))}
            </div>
          </BaSeGridItem>
        )}

        <BaSeGridItem size={12}>
          <BaSeAutocomplete
            key={projectKey}
            inputMode="search"
            label="Projeto:"
            minLengthToSearch={3}
            isDisabled={currentUf.length === 0}
            mode="list"
            onSearchTerm={handleAutocompleteSearchProject}
            onSelectedItem={(item, clear) => {
              setProjectSelected((prev) => ({
                ...prev,
                ...item,
                label: item.label
              }));
              setActionSelected(undefined);
              setActionKey((k) => k + 1);
              clear('');
            }}
            placeholder="Informe o projeto"
            subLabel=""
          />
        </BaSeGridItem>

        {projectSelected && (
          <BaSeGridItem size={12}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              <BaSeTag
                color="default"
                limitedWidth={300}
                label={projectSelected.label}
                onClose={() => {
                  setProjectSelected(undefined);
                  setActionSelected(undefined);
                  setActionKey((k) => k + 1);
                }}
                size="small"
                themeMode="light"
                type="primary"
              />
            </div>
          </BaSeGridItem>
        )}

        <BaSeGridItem size={12}>
          <BaSeAutocomplete
            key={actionKey}
            inputMode="search"
            label="Ação:"
            minLengthToSearch={3}
            isDisabled={!projectSelected}
            mode="list"
            onSearchTerm={handleAutocompleteSearchAction}
            onSelectedItem={(item, clear) => {
              setActionSelected((prev) => ({
                ...prev,
                ...item,
                label: item.label
              }));
              clear('');
            }}
            placeholder="Informe a ação"
            subLabel=""
          />
        </BaSeGridItem>

        {actionSelected && (
          <BaSeGridItem size={12}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              <BaSeTag
                color="default"
                label={actionSelected.label}
                onClose={() => setActionSelected(undefined)}
                size="small"
                themeMode="light"
                type="primary"
              />
            </div>
          </BaSeGridItem>
        )}
      </BaSeGrid>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginTop: 30,
        }}
      >
        {isEdit ? (
          <>
            <BaSeButton
              value="Salvar edição"
              onClick={() => handleSave('save')}
            />
            <BaSeButton
              value="Cancelar"
              color={BaSeTheme.colors.defaultColors.black}
              type="secondary"
              onClick={() => closeModal()}
            />
          </>
        ) : (
          <>
            <BaSeButton
              value="Salvar e criar"
              onClick={() => handleSave('save')}
            />
            <BaSeButton
              value="Salvar e criar nova"
              type="secondary"
              onClick={() => handleSave('new')}
            />
          </>
        )}
      </div>
    </>
  );
}

export default RegionalForm;
